import React from 'react';
import clsx from 'clsx';
import { Theme } from '@mui/material/styles';
import { Box, Typography, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { LabelImportant } from '@mui/icons-material';
import { blue, blueGrey, green, lightGreen } from '@mui/material/colors';

import { Stage } from '../../api';
import { StageStatus } from '../../types';

interface Props {
  stage: Stage;
  showLeft?: boolean;
  isUpdating: boolean;
  showRight?: boolean;
  status: StageStatus;
  onClick?: (stage: Stage) => void;
}

export const StageBadge = ({ stage, onClick, status, showLeft = true, showRight = true }: Props) => {
  const classes = useStyles({ status });

  return (
    <Box
      className={classes.wrapper}
      onClick={() => {
        onClick?.(stage);
      }}
    >
      {showLeft && <LabelImportant className={clsx(classes.icon, classes.leftIcon)} />}

      <Tooltip title={stage.name}>
        <Typography
          component="span"
          variant="subtitle2"
          sx={{
            zIndex: 10,
            display: 'block',
            overflow: 'hidden',
            position: 'relative',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {stage.name}
        </Typography>
      </Tooltip>

      {showRight && <LabelImportant className={clsx(classes.icon, classes.rightIcon)} />}
    </Box>
  );
};

const useStyles = makeStyles<Theme, { status: StageStatus }>(({ palette }) => {
  const stepColorByStatus: Record<StageStatus, string> = {
    [StageStatus.Viewed]: green[200],
    [StageStatus.Current]: blue[500],
    [StageStatus.Future]: blueGrey[50],
    [StageStatus.Passed]: lightGreen[50],
  };

  const textColorByStatus: Partial<Record<StageStatus, string>> = {
    [StageStatus.Current]: palette.common.white,
    [StageStatus.Future]: palette.text.disabled,
    [StageStatus.Viewed]: palette.common.white,
  };

  return {
    wrapper: {
      minWidth: '4rem',
      cursor: 'pointer',
      position: 'relative',
      borderRadius: '0.25rem',
      padding: '0.4rem 0.25rem 0.4rem 0.4rem',
      color: ({ status }) => textColorByStatus[status] ?? 'inherit',
      backgroundColor: ({ status }) => stepColorByStatus[status],

      '&:hover': {
        cursor: 'pointer',
      },
    },
    icon: {
      top: '50%',
      fontSize: '58px',
      position: 'absolute',
      transform: 'translateY(-50%)',
      color: ({ status }) => stepColorByStatus[status],
    },
    leftIcon: {
      left: '-24px',
    },
    rightIcon: {
      right: '-24px',
    },
  };
});
